"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { DesktopDownloadPlatform } from "@/src/features/downloads/desktopDownloads";

const NAV_ORBIT_PATH = "M1 27V19L19 1H177L195 19V35L177 53H19L1 35V27Z";
const NAV_STANDARD_WIDTH_CLASS = "w-[112px] sm:w-[120px] md:w-[130px] xl:w-[152px] 2xl:w-[184px]";
const NAV_LANGUAGE_WIDTH_CLASS = NAV_STANDARD_WIDTH_CLASS;
const NAV_SIGN_IN_WIDTH_CLASS = "w-[136px] sm:w-[144px] md:w-[146px] xl:w-[164px] 2xl:w-[196px]";
// A tab with an offer note grows from the standard width to fit the note, in any
// language, with room to keep the note clear of the frame's slanted corners.
const NAV_OFFER_WIDTH_CLASS = "min-w-[112px] sm:min-w-[120px] md:min-w-[130px] xl:min-w-[152px] 2xl:min-w-[184px]";
// Phones and tablets: the tabs share the row equally, as the desktop's fixed
// tabs are equal, and none gets narrower than its label (Pricing's offer note
// included), so a long label takes only the width it needs and the rest stay
// even. The padding is the least that keeps a 640px tablet's English row, Get
// started's busy label included, on one line.
const NAV_FLUID_CONTROL_CLASS = "flex-1 basis-0";
const NAV_PADDING_CLASS = { fixed: "px-3", fluid: "px-2" } as const;
const NAV_OFFER_PADDING_CLASS = { fixed: "px-5", fluid: "px-4" } as const;
const NAV_HANDOFF_DURATION_MS = 360;
const NAV_HANDOFF_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

export type LandingDesktopNavAction = {
  key: string;
  label: string;
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  pressed?: boolean;
  expanded?: boolean;
  busy?: boolean;
  /** Overrides the visible label when it alone is ambiguous ("English"). */
  ariaLabel?: string;
  /** Hover or focus: the visitor is about to open this, so warm it. */
  onIntent?: () => void;
  /**
   * The intro offer ("43% off your first 3 months"): a small red line under
   * the label, and a red light that orbits the tab's frame until the cyan
   * frame of a hovered, focused or open tab takes its place.
   */
  offer?: string;
};

export type LandingDownloadActionItem<TKey extends string> = {
  kind: "action";
  key: TKey;
  label: string;
  onSelect: () => void | Promise<void>;
  disabled: boolean;
  busy?: boolean;
  emphasized?: boolean;
};

export type LandingDownloadLinkItem<TKey extends string> = {
  kind: "link";
  key: TKey;
  label: string;
  href: string;
};

export type LandingDownloadUnavailableItem<TKey extends string> = {
  kind: "unavailable";
  key: TKey;
  label: string;
  tag: string;
};

export type LandingDesktopDownloadMenu = {
  desktop: {
    label: string;
    items: LandingDownloadActionItem<DesktopDownloadPlatform>[];
  };
  mobile: {
    label: string;
    items: Array<
      LandingDownloadLinkItem<"ios" | "android">
      | LandingDownloadUnavailableItem<"ios" | "android">
    >;
  };
  web: {
    label: string;
    item: LandingDownloadActionItem<"web">;
  };
  smartManagerOS: {
    label: string;
    item: LandingDownloadUnavailableItem<"smart-manager-os">;
  };
};

export { orderLandingDesktopDownloadItems } from "@/app/components/public/landing/landingDownloadOrder";

export type LandingNavSignIn = {
  label: string;
  busyLabel: string;
  onClick: () => void;
  isBusy: boolean;
};

type LandingDesktopNavigationProps = {
  actions: LandingDesktopNavAction[];
  /** Opens the Language window; labelled with the current language's own name. */
  language?: LandingDesktopNavAction;
  /** Opens the Download window; omitted inside the desktop app. */
  download?: LandingDesktopNavAction;
  /** Omitted where the page carries its own Get started call to action. */
  signIn?: LandingNavSignIn;
  /** Tabs share the full row instead of keeping fixed desktop widths. */
  fluid?: boolean;
};

export type LandingNavControlBounds = {
  key: string;
  left: number;
  right: number;
  top: number;
  bottom: number;
};

export type LandingNavBridge = {
  fromKey: string;
  toKey: string;
  left: number;
  width: number;
  direction: "left" | "right";
};

type LandingNavHandoff = LandingNavBridge & {
  token: number;
  fromOffset: number;
  fromWidth: number;
  toOffset: number;
  toWidth: number;
};

type LandingNavHandoffPaths = {
  top: string;
  bottom: string;
};

type LandingNavActiveFrameBounds = {
  left: number;
  width: number;
};

export function getLandingNavBridge(
  bounds: LandingNavControlBounds[],
  fromKey: string | null,
  toKey: string,
): LandingNavBridge | null {
  if (!fromKey || fromKey === toKey) return null;

  const fromIndex = bounds.findIndex((entry) => entry.key === fromKey);
  const toIndex = bounds.findIndex((entry) => entry.key === toKey);
  if (fromIndex < 0 || toIndex < 0 || Math.abs(fromIndex - toIndex) !== 1) return null;

  const leftControl = bounds[Math.min(fromIndex, toIndex)];
  const rightControl = bounds[Math.max(fromIndex, toIndex)];
  const gapWidth = rightControl.left - leftControl.right;
  if (gapWidth <= 0) return null;

  return {
    fromKey,
    toKey,
    left: leftControl.right,
    width: gapWidth,
    direction: toIndex > fromIndex ? "right" : "left",
  };
}

function getLandingNavHandoffPaths(handoff: LandingNavHandoff): LandingNavHandoffPaths {
  const direction = handoff.direction === "right" ? 1 : -1;
  const startX = handoff.fromOffset
    + handoff.fromWidth * (handoff.direction === "right" ? 0.28 : 0.72);
  const endX = handoff.toOffset
    + handoff.toWidth * (handoff.direction === "right" ? 0.72 : 0.28);
  const fromEdge = handoff.direction === "right"
    ? handoff.fromOffset + handoff.fromWidth
    : handoff.fromOffset;
  const toEdge = handoff.direction === "right"
    ? handoff.toOffset
    : handoff.toOffset + handoff.toWidth;
  const gapCenter = (fromEdge + toEdge) / 2;
  const outerLead = gapCenter - direction * 9;
  const innerLead = gapCenter - direction * 4;
  const innerTrail = gapCenter + direction * 4;
  const outerTrail = gapCenter + direction * 9;

  return {
    top: `M${startX} 1H${outerLead}L${innerLead} 6H${innerTrail}L${outerTrail} 1H${endX}`,
    bottom: `M${startX} 53H${outerLead}L${innerLead} 48H${innerTrail}L${outerTrail} 53H${endX}`,
  };
}

function LandingNavActiveFrame({ paused }: { paused: boolean }) {
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
      preserveAspectRatio="none"
      viewBox="0 0 196 54"
    >
      <path
        className={`landingDesktopNavOrbitTracer landingDesktopNavOrbitTracerOne ${paused ? "landingDesktopNavOrbitTracerPaused" : ""}`}
        d={NAV_ORBIT_PATH}
        fill="none"
        pathLength="100"
        stroke="#00E6EE"
        strokeDasharray="24 76"
        strokeLinecap="square"
        strokeWidth="1.4"
        vectorEffect="non-scaling-stroke"
      />
      <path
        className={`landingDesktopNavOrbitTracer landingDesktopNavOrbitTracerTwo ${paused ? "landingDesktopNavOrbitTracerPaused" : ""}`}
        d={NAV_ORBIT_PATH}
        fill="none"
        pathLength="100"
        stroke="#00E6EE"
        strokeDasharray="24 76"
        strokeLinecap="square"
        strokeWidth="1.4"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

/** The offer tab's standing red light: the active frame's two tracers, in red, on a faint red outline. */
function LandingNavOfferTracer({ hidden }: { hidden: boolean }) {
  return (
    <svg
      aria-hidden="true"
      data-landing-nav-offer-tracer="true"
      className={`landingDesktopNavOfferTracer pointer-events-none absolute inset-0 h-full w-full overflow-visible ${hidden ? "landingDesktopNavOfferTracerHidden" : ""}`}
      preserveAspectRatio="none"
      viewBox="0 0 196 54"
    >
      <path
        d={NAV_ORBIT_PATH}
        fill="none"
        stroke="rgba(255, 61, 90, 0.18)"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
      {(["One", "Two"] as const).map((tracer) => (
        <path
          key={tracer}
          className={`landingDesktopNavOrbitTracer landingDesktopNavOfferOrbit landingDesktopNavOrbitTracer${tracer}`}
          d={NAV_ORBIT_PATH}
          fill="none"
          pathLength="100"
          stroke="#FF3D5A"
          strokeDasharray="24 76"
          strokeLinecap="square"
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
}

export default function LandingDesktopNavigation({
  actions,
  language,
  download,
  signIn,
  fluid = false,
}: LandingDesktopNavigationProps) {
  const navRef = useRef<HTMLElement>(null);
  const controlRefs = useRef(new Map<string, HTMLElement>());
  const activeKeyRef = useRef<string | null>(null);
  const handoffRef = useRef<LandingNavHandoff | null>(null);
  const handoffSettleTimerRef = useRef<number | null>(null);
  const handoffTokenRef = useRef(0);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [activeFrame, setActiveFrame] = useState<LandingNavActiveFrameBounds | null>(null);
  const [handoff, setHandoff] = useState<LandingNavHandoff | null>(null);

  // The open window's tab. The frame previews whatever the pointer or keyboard
  // is on, then always settles back here (or clears when nothing is open).
  const selectedKey = [...actions, ...(language ? [language] : []), ...(download ? [download] : [])]
    .find((action) => action.pressed || action.expanded)?.key ?? null;
  const selectedKeyRef = useRef<string | null>(selectedKey);
  const pointerInsideRef = useRef(false);

  const languageKey = language?.key;
  const hasSignIn = Boolean(signIn);
  const order = useMemo(() => [
    ...actions.map((action) => action.key),
    ...(languageKey ? [languageKey] : []),
    ...(download ? [download.key] : []),
    ...(hasSignIn ? ["sign-in"] : []),
  ], [actions, download, hasSignIn, languageKey]);

  const updateHandoff = useCallback((nextHandoff: LandingNavHandoff | null) => {
    handoffRef.current = nextHandoff;
    setHandoff(nextHandoff);
  }, []);

  const clearHandoffSettleTimer = useCallback(() => {
    if (handoffSettleTimerRef.current === null) return;
    window.clearTimeout(handoffSettleTimerRef.current);
    handoffSettleTimerRef.current = null;
  }, []);

  const completeHandoff = useCallback(() => {
    clearHandoffSettleTimer();
    updateHandoff(null);
  }, [clearHandoffSettleTimer, updateHandoff]);

  const getControlBounds = useCallback((): LandingNavControlBounds[] => order.flatMap((key) => {
    const element = controlRefs.current.get(key);
    if (!element) return [];
    const rect = element.getBoundingClientRect();
    return [{ key, left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom }];
  }), [order]);

  const getActiveFrameBounds = useCallback((key: string): LandingNavActiveFrameBounds | null => {
    const nav = navRef.current;
    const control = controlRefs.current.get(key);
    if (!nav || !control) return null;

    const navRect = nav.getBoundingClientRect();
    const controlRect = control.getBoundingClientRect();
    return {
      left: controlRect.left - navRect.left,
      width: controlRect.width,
    };
  }, []);

  const beginHandoff = useCallback((nextBridge: LandingNavBridge) => {
    const nav = navRef.current;
    const fromControl = controlRefs.current.get(nextBridge.fromKey);
    const toControl = controlRefs.current.get(nextBridge.toKey);
    if (!nav || !fromControl || !toControl) {
      completeHandoff();
      return;
    }

    clearHandoffSettleTimer();
    const navRect = nav.getBoundingClientRect();
    const fromRect = fromControl.getBoundingClientRect();
    const toRect = toControl.getBoundingClientRect();
    const left = Math.min(fromRect.left, toRect.left);
    const right = Math.max(fromRect.right, toRect.right);
    const token = handoffTokenRef.current + 1;
    handoffTokenRef.current = token;

    updateHandoff({
      ...nextBridge,
      token,
      left: left - navRect.left,
      width: right - left,
      fromOffset: fromRect.left - left,
      fromWidth: fromRect.width,
      toOffset: toRect.left - left,
      toWidth: toRect.width,
    });

    handoffSettleTimerRef.current = window.setTimeout(() => {
      handoffSettleTimerRef.current = null;
      if (handoffRef.current?.token === token) updateHandoff(null);
    }, NAV_HANDOFF_DURATION_MS);
  }, [clearHandoffSettleTimer, completeHandoff, updateHandoff]);

  const setActiveControl = useCallback((key: string) => {
    const previousKey = activeKeyRef.current;
    if (previousKey === key) return;

    const nextBridge = getLandingNavBridge(getControlBounds(), previousKey, key);
    if (nextBridge) beginHandoff(nextBridge);
    else completeHandoff();

    const nextFrame = getActiveFrameBounds(key);
    if (nextFrame) setActiveFrame(nextFrame);

    activeKeyRef.current = key;
    setActiveKey(key);
  }, [beginHandoff, completeHandoff, getActiveFrameBounds, getControlBounds]);

  const clearActiveControl = useCallback(() => {
    completeHandoff();
    activeKeyRef.current = null;
    setActiveKey(null);
    setActiveFrame(null);
  }, [completeHandoff]);

  /** Where the frame rests when nothing previews: a keyboard-focused tab, else the open window's. */
  const settleActiveControl = useCallback(() => {
    if (pointerInsideRef.current) return;
    const focused = document.activeElement;
    const focusedKey = focused instanceof HTMLElement
      && navRef.current?.contains(focused)
      && focused.matches(":focus-visible")
      ? focused.dataset.landingNavKey ?? null
      : null;
    const restingKey = focusedKey ?? selectedKeyRef.current;
    if (restingKey) setActiveControl(restingKey);
    else clearActiveControl();
  }, [clearActiveControl, setActiveControl]);

  useEffect(() => {
    selectedKeyRef.current = selectedKey;
    settleActiveControl();
  }, [selectedKey, settleActiveControl]);

  const handleControlPointerEnter = useCallback((key: string) => {
    pointerInsideRef.current = true;
    setActiveControl(key);
  }, [setActiveControl]);

  const handlePointerLeave = useCallback(() => {
    pointerInsideRef.current = false;
    completeHandoff();
    settleActiveControl();
  }, [completeHandoff, settleActiveControl]);

  const handleBlur = useCallback((event: React.FocusEvent<HTMLElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return;
    settleActiveControl();
  }, [settleActiveControl]);

  useEffect(() => {
    return () => {
      if (handoffSettleTimerRef.current !== null) {
        window.clearTimeout(handoffSettleTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const syncFrameToLayout = () => {
      const key = activeKeyRef.current;
      if (!key) return;
      completeHandoff();
      const nextFrame = getActiveFrameBounds(key);
      if (nextFrame) setActiveFrame(nextFrame);
    };

    window.addEventListener("resize", syncFrameToLayout);
    return () => window.removeEventListener("resize", syncFrameToLayout);
  }, [completeHandoff, getActiveFrameBounds]);

  useEffect(() => {
    if (activeKeyRef.current && !order.includes(activeKeyRef.current)) clearActiveControl();
  }, [clearActiveControl, order]);

  const registerControl = (key: string, element: HTMLElement | null) => {
    if (element) controlRefs.current.set(key, element);
    else controlRefs.current.delete(key);
  };

  // Sentence-case labels on the shared body style; color comes from state.
  const getControlClassName = (widthClass: string, padding: { fixed: string; fluid: string } = NAV_PADDING_CLASS) => (
    `landingDesktopNavControl nv-body relative z-10 inline-flex h-[54px] ${fluid ? `${NAV_FLUID_CONTROL_CLASS} ${padding.fluid}` : `${widthClass} shrink-0 ${padding.fixed}`} items-center justify-center text-center font-medium whitespace-nowrap focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50`
  );

  // Every nav item is the same kind of control: it opens its own window and
  // reads as pressed while that window is the one in front.
  const renderAction = (action: LandingDesktopNavAction, widthClass: string) => {
    const selected = Boolean(action.pressed || action.expanded);
    const lit = selected || activeKey === action.key;
    return (
      <button
        key={action.key}
        ref={(element) => registerControl(action.key, element)}
        type="button"
        onPointerEnter={() => {
          handleControlPointerEnter(action.key);
          action.onIntent?.();
        }}
        onFocus={() => {
          setActiveControl(action.key);
          action.onIntent?.();
        }}
        onClick={() => {
          if (!action.disabled) void action.onClick();
        }}
        disabled={action.disabled}
        aria-pressed={action.pressed}
        aria-expanded={action.expanded}
        aria-busy={action.busy}
        aria-label={action.ariaLabel}
        data-landing-nav-key={action.key}
        className={`${(action.offer
          ? getControlClassName(NAV_OFFER_WIDTH_CLASS, NAV_OFFER_PADDING_CLASS)
          : getControlClassName(widthClass))} ${
          lit
            ? "text-cyan-50"
            : action.offer ? "text-white/85 hover:text-white" : "text-white/55 hover:text-white/85"
        }`}
      >
        {action.offer ? (
          <>
            <LandingNavOfferTracer hidden={lit} />
            {/* In the flow, so the tab is as wide as the note in any language. */}
            <span className="relative z-10 flex flex-col items-center">
              <span className="leading-[18px]">{action.label}</span>
              <span
                data-landing-nav-offer="true"
                className="text-[11px] leading-[14px] font-semibold text-[#FF4D66] [text-shadow:0_0_8px_rgba(255,61,90,0.6)]"
              >
                {action.offer}
              </span>
            </span>
          </>
        ) : (
          <span className="relative z-10">{action.label}</span>
        )}
      </button>
    );
  };

  const handoffPaths = handoff ? getLandingNavHandoffPaths(handoff) : null;

  return (
    <nav
      ref={navRef}
      aria-label="Primary"
      data-landing-desktop-navigation="true"
      data-landing-nav-active-key={activeKey ?? undefined}
      data-landing-nav-handoff-state={handoff ? "traveling" : activeFrame ? "orbiting" : "idle"}
      data-landing-nav-motion="pointer-led"
      data-landing-nav-proportions="balanced"
      data-landing-nav-connector-lifecycle="handoff-only"
      data-landing-nav-sign-in-style="shared"
      data-landing-nav-tracer-flow="pointer-led-paired"
      onPointerLeave={handlePointerLeave}
      onBlur={handleBlur}
      className="relative isolate flex h-[54px] items-center gap-1.5 sm:gap-2"
    >
      {activeFrame && (
        <span
          aria-hidden="true"
          data-landing-nav-active-frame="true"
          className={`landingDesktopNavActiveLayer pointer-events-none absolute inset-y-0 left-0 z-0 block ${
            handoff ? "landingDesktopNavActiveLayerHandoff" : ""
          }`}
          style={{
            width: activeFrame.width,
            transform: `translate3d(${activeFrame.left}px, 0, 0)`,
          }}
        >
          <span className="landingDesktopNavSurface absolute inset-0" />
          <LandingNavActiveFrame paused={Boolean(handoff)} />
        </span>
      )}

      {handoff && handoffPaths && (
        <svg
          key={handoff.token}
          aria-hidden="true"
          data-landing-nav-handoff={`${handoff.fromKey}:${handoff.toKey}`}
          data-landing-nav-handoff-phase="traveling"
          className="landingDesktopNavHandoffLayer pointer-events-none absolute left-0 top-0 z-20 h-[54px] overflow-visible"
          style={{
            width: handoff.width,
            transform: `translate3d(${handoff.left}px, 0, 0)`,
          }}
          preserveAspectRatio="none"
          viewBox={`0 0 ${Math.max(1, handoff.width)} 54`}
        >
          <g className="landingDesktopNavHandoff">
            <path
              className="landingDesktopNavHandoffTracer"
              d={handoffPaths.top}
              pathLength="100"
              stroke="#00E6EE"
              strokeDasharray="26 74"
              vectorEffect="non-scaling-stroke"
            />
            <path
              className="landingDesktopNavHandoffTracer"
              d={handoffPaths.bottom}
              pathLength="100"
              stroke="#00E6EE"
              strokeDasharray="26 74"
              vectorEffect="non-scaling-stroke"
            />
          </g>
        </svg>
      )}

      {actions.map((action) => renderAction(action, NAV_STANDARD_WIDTH_CLASS))}
      {language && renderAction(language, NAV_LANGUAGE_WIDTH_CLASS)}
      {download && renderAction(download, NAV_STANDARD_WIDTH_CLASS)}

      {signIn && <button
        ref={(element) => registerControl("sign-in", element)}
        type="button"
        aria-busy={signIn.isBusy}
        disabled={signIn.isBusy}
        onPointerEnter={() => handleControlPointerEnter("sign-in")}
        onFocus={() => setActiveControl("sign-in")}
        onClick={signIn.onClick}
        data-landing-nav-key="sign-in"
        className={`${getControlClassName(NAV_SIGN_IN_WIDTH_CLASS)} ${
          activeKey === "sign-in" ? "text-cyan-50" : "text-white/55 hover:text-white/85"
        }`}
      >
        {/* Sized for the wider of the two labels, so going busy never resizes the control or reflows the row. */}
        <span className="relative z-10 grid justify-items-center">
          <span aria-live="polite" className="col-start-1 row-start-1">
            {signIn.isBusy ? signIn.busyLabel : signIn.label}
          </span>
          <span aria-hidden="true" className="invisible col-start-1 row-start-1">
            {signIn.isBusy ? signIn.label : signIn.busyLabel}
          </span>
        </span>
      </button>}

      <style jsx>{`
        .landingDesktopNavControl {
          background: transparent;
          transition: color 180ms ease, filter 180ms ease, transform 180ms ease;
        }

        .landingDesktopNavControl:hover {
          filter: drop-shadow(0 0 8px rgba(0, 230, 238, 0.12));
        }

        .landingDesktopNavControl:active {
          transform: scale(0.975);
        }

        .landingDesktopNavSurface {
          clip-path: polygon(9.7% 0, 90.3% 0, 100% 35.2%, 100% 64.8%, 90.3% 100%, 9.7% 100%, 0 64.8%, 0 35.2%);
          background: radial-gradient(circle at 50% 50%, rgba(0, 230, 238, 0.07), rgba(2, 8, 19, 0.025) 70%);
          box-shadow: inset 0 0 20px rgba(0, 230, 238, 0.035);
        }

        .landingDesktopNavActiveLayer {
          opacity: 1;
          transition: opacity 70ms ease-out;
          will-change: opacity;
        }

        .landingDesktopNavActiveLayerHandoff {
          opacity: 0;
          transition: none;
        }

        .landingDesktopNavHandoffLayer {
          will-change: contents;
        }

        :global(.landingDesktopNavOrbitTracer) {
          animation-duration: 5.6s;
          animation-iteration-count: infinite;
          animation-timing-function: linear;
          filter: drop-shadow(0 0 5px rgba(0, 230, 238, 0.76));
          opacity: 1;
          transition: opacity 70ms ease-out;
          will-change: stroke-dashoffset;
        }

        :global(.landingDesktopNavOrbitTracerOne) {
          animation-name: landing-desktop-nav-orbit-one;
        }

        :global(.landingDesktopNavOrbitTracerTwo) {
          animation-name: landing-desktop-nav-orbit-two;
        }

        :global(.landingDesktopNavOfferOrbit) {
          filter: drop-shadow(0 0 5px rgba(255, 61, 90, 0.85));
        }

        :global(.landingDesktopNavOfferTracer) {
          opacity: 1;
          transition: opacity 180ms ease;
        }

        :global(.landingDesktopNavOfferTracerHidden) {
          opacity: 0;
        }

        :global(.landingDesktopNavOfferTracerHidden .landingDesktopNavOrbitTracer) {
          animation-play-state: paused;
        }

        :global(.landingDesktopNavOrbitTracerPaused) {
          animation-play-state: paused;
          opacity: 0;
        }

        .landingDesktopNavHandoff {
          fill: none;
          stroke-linecap: square;
          stroke-width: 1.4;
          filter: drop-shadow(0 0 5px rgba(0, 230, 238, 0.72));
        }

        .landingDesktopNavHandoffTracer {
          animation: landing-desktop-nav-handoff-travel ${NAV_HANDOFF_DURATION_MS}ms ${NAV_HANDOFF_EASING} both;
          stroke-dashoffset: 0;
          will-change: stroke-dashoffset;
        }

        @keyframes landing-desktop-nav-handoff-travel {
          from { stroke-dashoffset: 0; }
          to { stroke-dashoffset: -74; }
        }

        @keyframes landing-desktop-nav-orbit-one {
          from { stroke-dashoffset: 0; }
          to { stroke-dashoffset: -100; }
        }

        @keyframes landing-desktop-nav-orbit-two {
          from { stroke-dashoffset: -50; }
          to { stroke-dashoffset: -150; }
        }

        @media (prefers-reduced-motion: reduce) {
          .landingDesktopNavControl,
          .landingDesktopNavActiveLayer,
          .landingDesktopNavHandoffLayer,
          :global(.landingDesktopNavOfferTracer) {
            transition: none;
          }

          .landingDesktopNavHandoffTracer,
          :global(.landingDesktopNavOrbitTracer) {
            animation: none !important;
          }

          .landingDesktopNavHandoffTracer { stroke-dashoffset: -74; }
        }
      `}</style>
    </nav>
  );
}
